import { useCallback, useState, type ReactNode } from 'react';
import { useBuildStamp } from '../lib/useBuildStamp';
import { formatBuildTimeDisplay } from '../lib/buildStamp';
import { getResolvedApiBaseUrl } from '../lib/api';
import { isLocalAppEnv } from '../lib/runtimeEnv';
import { Toast } from './Feedback';
import { SelfTestPanel } from './SelfTestPanel';
import { HomeScreenBanner } from './HomeScreenBanner';
import { useAppStore } from '../store/useAppStore';
import { CheckIcon, WarnIcon, CopyIcon, SettingsIcon } from './icons';

function copyDiagnosticsToClipboard(build: { sha: string; buildTime: string; env?: string }): Promise<boolean> {
  const apiBase = getResolvedApiBaseUrl();
  const payload = {
    git_sha: build.sha,
    build_time: build.buildTime,
    env: build.env ?? (import.meta.env.MODE === 'production' ? 'production' : 'development'),
    api_base: apiBase ?? '(not set)',
  };
  const text = JSON.stringify(payload, null, 2);
  if (typeof navigator?.clipboard?.writeText === 'function') {
    return navigator.clipboard.writeText(text).then(() => true).catch(() => false);
  }
  return Promise.resolve(false);
}

interface AppLayoutProps {
  children: ReactNode;
  showSelfTest?: boolean;
  onSelfTestClose?: () => void;
  onOpenProject?: (id: string) => void;
}

export function AppLayout({ children, showSelfTest = false, onSelfTestClose, onOpenProject }: AppLayoutProps) {
  const build = useBuildStamp();
  const showLocalDiagnostics = isLocalAppEnv();
  const [selfTestVisible, setSelfTestVisible] = useState(showLocalDiagnostics && showSelfTest);
  const { notifications, dismissNotification, screen } = useAppStore();
  const firstNotification = notifications[0];
  const dismissToast = () => firstNotification && dismissNotification(firstNotification.id);

  const [copyStatus, setCopyStatus] = useState<'idle' | 'ok' | 'error'>('idle');
  const handleCopyDiagnostics = useCallback(async () => {
    setCopyStatus('idle');
    const ok = await copyDiagnosticsToClipboard({
      sha: build.sha,
      buildTime: build.buildTime,
      env: build.env,
    });
    setCopyStatus(ok ? 'ok' : 'error');
    window.setTimeout(() => setCopyStatus('idle'), 2000);
  }, [build.sha, build.buildTime, build.env]);

  return (
    <div className={`container${selfTestVisible ? ' selftest-enabled' : ''}`}>
      {firstNotification && <Toast message={firstNotification.message} onDismiss={dismissToast} />}
      {screen === 'list' && <HomeScreenBanner />}
      {children}
      {showLocalDiagnostics && selfTestVisible && onOpenProject && (
        <SelfTestPanel onOpenProject={onOpenProject} onClose={onSelfTestClose ?? (() => setSelfTestVisible(false))} defaultOpen={true} />
      )}
      {showLocalDiagnostics && (
        <div
          className="build-stamp"
          title={[
            `build: ${build.shortSha}`,
            getResolvedApiBaseUrl() ? `API: ${getResolvedApiBaseUrl()}` : 'API: not set',
            `origin: ${typeof window !== 'undefined' ? window.location.origin : ''}`,
          ].join('\n')}
        >
          <>
            build {build.shortSha} <span className="build-stamp-sep">·</span> {formatBuildTimeDisplay(build.buildTime)}
            <button type="button" className="build-stamp-btn" onClick={handleCopyDiagnostics} aria-label="Copy diagnostics" title="Copy diagnostics (build_sha, api_base, origin, time)">
              {copyStatus === 'ok' ? <CheckIcon size={12} /> : copyStatus === 'error' ? <WarnIcon size={12} /> : <CopyIcon size={12} />}
            </button>
            <button type="button" className="build-stamp-btn" onClick={() => setSelfTestVisible((v) => !v)} aria-label="Toggle self-test" title="Диагностика"><SettingsIcon /></button>
          </>
        </div>
      )}
    </div>
  );
}
